import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Hash, User, Plus, Search, Paperclip, Image, Smile, MoreVertical, Users, Lock, Globe, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { baseURL } from '@/config/APIConfig';


const API = baseURL.AiBaseURL;

const Chat = ({ isOpen, onClose, onUnreadCountChange }) => {
    const [activeView, setActiveView] = useState('channels'); // 'channels' or 'direct'
    const [channels, setChannels] = useState([]);
    const [directMessages, setDirectMessages] = useState([]);
    const [activeChannel, setActiveChannel] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageInput, setMessageInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [showChannelModal, setShowChannelModal] = useState(false);
    const [showUserList, setShowUserList] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const wsRef = useRef(null);

    const currentUser = localStorage.getItem('username') || 'demo-user';

    useEffect(() => {
        if (isOpen) {
            fetchChannels();
            fetchDirectMessages();
            fetchUsers();
            connectWebSocket();
            // Mark all messages as read when chat is opened
            markAllAsRead();
        }
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, [isOpen]);

    useEffect(() => {
        if (activeChannel) {
            fetchMessages(activeChannel.id);

            // Poll for new messages every 3 seconds
            const pollInterval = setInterval(() => {
                fetchMessages(activeChannel.id);
            }, 3000);

            return () => clearInterval(pollInterval);
        }
    }, [activeChannel]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const connectWebSocket = () => {
        // WebSocket connection for real-time updates
        const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
        const ws = new WebSocket(`${wsUrl}/ws/chat`);

        ws.onopen = () => {
            console.log('WebSocket connected');
        };

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            if (data.type === 'new_message' && data.channelId === activeChannel?.id) {
                setMessages(prev => [...prev, data.message]);
            }
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        ws.onclose = () => {
            console.log('WebSocket disconnected');
            // Reconnect after 3 seconds
            setTimeout(connectWebSocket, 3000);
        };

        wsRef.current = ws;
    };

    const fetchChannels = async () => {
        try {
            const response = await axios.get(`${API}/api/chat/channels`);
            setChannels(response.data.channels || []);
        } catch (error) {
            console.error('Error fetching channels:', error);
            // Fallback to mock data if API fails
            const mockChannels = [
                {
                    id: 'channel_general',
                    name: 'general',
                    description: 'General team discussion',
                    private: false,
                    type: 'channel',
                    members: [],
                },
                {
                    id: 'channel_logistics',
                    name: 'logistics',
                    description: 'Logistics and operations',
                    private: false,
                    type: 'channel',
                    members: [],
                }
            ];
            setChannels(mockChannels);
        }
    };

    const fetchDirectMessages = async () => {
        try {
            const response = await axios.get(`${API}/api/chat/direct-messages`);
            setDirectMessages(response.data.conversations || []);
        } catch (error) {
            console.error('Error fetching direct messages:', error);
            setDirectMessages([]);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API}/api/chat/users`);
            const allUsers = response.data.users || [];
            // Filter out current user from the list
            setUsers(allUsers.filter(u => u.id !== currentUser));
        } catch (error) {
            console.error('Error fetching users:', error);
            // Fallback to mock users
            const mockUsers = [
                { id: 'sam', name: 'Sam Wilson', email: 'sam@shipez.ai', online: true },
                { id: 'alok', name: 'Alok Kumar', email: 'alok@shipez.ai', online: true },
                { id: 'dev', name: 'Dev User', email: 'dev@shipez.ai', online: true },
            ];
            setUsers(mockUsers.filter(u => u.id !== currentUser));
        }
    };

    const fetchMessages = async (channelId) => {
        try {
            const response = await axios.get(`${API}/api/chat/messages/${channelId}`);
            console.log('Fetched messages:', response);
            setMessages(response.data.messages || []);
        } catch (error) {
            console.error('Error fetching messages:', error);
            setMessages([]);
        }
    };

    const sendMessage = async () => {
        if (!messageInput.trim() && !selectedFile) return;
        if (!activeChannel) return;

        try {
            const formData = new FormData();
            formData.append('channelId', activeChannel.id);
            formData.append('message', messageInput);
            formData.append('sender', currentUser);
            if (selectedFile) {
                formData.append('file', selectedFile);
            }

            const response = await axios.post(`${API}/api/chat/send`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            // Refresh messages to show the new message
            await fetchMessages(activeChannel.id);

            setMessageInput('');
            setSelectedFile(null);
            toast.success('Message sent!');
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message');
        }
    };

    const createChannel = async (channelData) => {
        try {
            await axios.post(`${API}/api/chat/channels`, channelData);
            await fetchChannels();
            setShowChannelModal(false);
            toast.success('Channel created successfully');
        } catch (error) {
            console.error('Error creating channel:', error);
            toast.error('Failed to create channel');
        }
    };

    const startDirectMessage = async (userId) => {
        try {
            console.log('Starting direct message with userId:', userId, 'and currentUser:', currentUser);
            const response = await axios.post(`${API}/api/chat/direct-message/start`, {
                userId,
                currentUser
            });

            const conversation = response.data.conversation;
            console.log('Started conversation:', conversation);
            // Add to direct messages list if not already there
            const exists = directMessages.find(dm => dm.id === conversation.id);
            if (!exists) {
                setDirectMessages([...directMessages, conversation]);
            }

            setActiveChannel(conversation);
            setActiveView('direct');
            setShowUserList(false);

            const user = users.find(u => u.id === userId);
            toast.success(`Started conversation with ${user?.name || userId}`);
        } catch (error) {
            console.error('Error starting direct message:', error);
            toast.error('Failed to start conversation');
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            toast.success(`File selected: ${file.name}`);
        }
    };

    const markAllAsRead = () => {
        // Update the last checked timestamp when chat is opened
        const now = new Date().toISOString();
        localStorage.setItem('chat_last_checked', now);
        // Notify parent component to update unread count
        if (onUnreadCountChange) {
            onUnreadCountChange(0);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99999] flex items-center justify-center p-2 sm:p-4 lg:p-6"
                    onClick={onClose}
                    style={{ margin: 0 }}
                >
                    <motion.div
                        initial={{ scale: 0.92, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.92, opacity: 0 }}
                        transition={{ type: "spring", damping: 20, stiffness: 200 }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-6xl h-[95vh] sm:h-[85vh] flex flex-col sm:flex-row overflow-hidden border border-slate-200 dark:border-slate-700"
                        style={{ position: 'relative' }}
                    >
                        {/* Mobile Header - Only visible on small screens */}
                        <div className="sm:hidden bg-gradient-to-r from-purple-600 to-indigo-700 p-4 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-white flex items-center gap-2">
                                <Users className="w-5 h-5" />
                                Team Chat
                            </h2>
                            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
                                <X className="w-5 h-5 text-white" />
                            </button>
                        </div>

                        {/* Sidebar - Hidden on mobile when chat is active */}
                        <div className={`${activeChannel && 'hidden sm:flex'} w-full sm:w-64 lg:w-72 bg-gradient-to-b from-purple-600 to-indigo-700 flex flex-col`}>
                            {/* Header - Hidden on mobile */}
                            <div className="hidden sm:block p-4 lg:p-6 border-b border-white/10">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-lg lg:text-xl font-bold text-white flex items-center gap-2">
                                        <Users className="w-5 h-5 lg:w-6 lg:h-6" />
                                        Team Chat
                                    </h2>
                                    <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg sm:hidden lg:block">
                                        <X className="w-5 h-5 text-white" />
                                    </button>
                                </div>
                                <p className="text-xs text-purple-100 mt-1">Real-time collaboration</p>
                            </div>

                            {/* Navigation */}
                            <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                                <div className="p-3 sm:p-4">
                                    {/* Channels Section */}
                                    <div className="mb-4 sm:mb-6">
                                        <div className="flex items-center justify-between mb-2 sm:mb-3">
                                            <button
                                                onClick={() => setActiveView('channels')}
                                                className="text-xs sm:text-sm font-bold text-white/90 hover:text-white flex items-center gap-2"
                                            >
                                                <Hash className="w-4 h-4" />
                                                Channels
                                            </button>
                                            <button
                                                onClick={() => setShowChannelModal(true)}
                                                className="w-6 h-6 rounded-md bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
                                            >
                                                <Plus className="w-4 h-4" />
                                            </button>
                                        </div>
                                        {activeView === 'channels' && (
                                            <div className="space-y-1">
                                                {channels.map((channel) => (
                                                    <button
                                                        key={channel.id}
                                                        onClick={() => setActiveChannel(channel)}
                                                        className={`w-full text-left px-3 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm transition-all ${activeChannel?.id === channel.id
                                                            ? 'bg-white text-purple-700 font-semibold shadow-md'
                                                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                                                            }`}
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            {channel.private ? <Lock className="w-3.5 h-3.5" /> : <Hash className="w-3.5 h-3.5" />}
                                                            {channel.name}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Direct Messages Section */}
                                    <div>
                                        <div className="flex items-center justify-between mb-2 sm:mb-3">
                                            <button
                                                onClick={() => setActiveView('direct')}
                                                className="text-xs sm:text-sm font-bold text-white/90 hover:text-white flex items-center gap-2"
                                            >
                                                <User className="w-4 h-4" />
                                                Direct Messages
                                            </button>
                                            <button
                                                onClick={() => setShowUserList(true)}
                                                className="w-6 h-6 rounded-md bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
                                            >
                                                <Plus className="w-4 h-4" />
                                            </button>
                                        </div>
                                        {activeView === 'direct' && (
                                            <div className="space-y-1">
                                                {directMessages.map((dm) => (
                                                    <button
                                                        key={dm.id}
                                                        onClick={() => setActiveChannel(dm)}
                                                        className={`w-full text-left px-3 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm transition-all ${activeChannel?.id === dm.id
                                                            ? 'bg-white text-purple-700 font-semibold shadow-md'
                                                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                                                            }`}
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            <User className="w-3.5 h-3.5" />
                                                            {dm.name}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Main Chat Area */}
                        <div className="flex-1 flex flex-col bg-slate-50 dark:bg-slate-950">
                            {/* Chat Header */}
                            <div className="h-16 border-b border-slate-200 dark:border-slate-800 px-6 flex items-center justify-between bg-white dark:bg-slate-900 shadow-sm">
                                <div className="flex items-center gap-3">
                                    {activeChannel ? (
                                        <>
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${activeChannel.type === 'channel'
                                                ? 'bg-gradient-to-br from-purple-500 to-indigo-600'
                                                : 'bg-gradient-to-br from-blue-500 to-cyan-600'
                                                }`}>
                                                {activeChannel.private ? (
                                                    <Lock className="w-5 h-5 text-white" />
                                                ) : activeChannel.type === 'channel' ? (
                                                    <Hash className="w-5 h-5 text-white" />
                                                ) : (
                                                    <User className="w-5 h-5 text-white" />
                                                )}
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-slate-900 dark:text-white text-lg">
                                                    {activeChannel.name}
                                                </h3>
                                                {activeChannel.description && (
                                                    <p className="text-xs text-slate-500">{activeChannel.description}</p>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <span className="text-slate-500 italic">Select a channel or conversation to start chatting</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Search messages..."
                                            className="pl-10 pr-4 py-2 bg-slate-100 dark:bg-slate-800 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 w-64"
                                        />
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="p-2 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Messages Area */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                {!activeChannel ? (
                                    <div className="flex items-center justify-center h-full">
                                        <div className="text-center">
                                            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                                                <MessageCircle className="w-10 h-10 text-white" />
                                            </div>
                                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Welcome to Team Chat!</h3>
                                            <p className="text-slate-500 max-w-md">
                                                Select a channel from the sidebar to start messaging with your team or create a new channel.
                                            </p>
                                        </div>
                                    </div>
                                ) : messages.length === 0 ? (
                                    <div className="flex items-center justify-center h-full">
                                        <div className="text-center">
                                            <p className="text-slate-500">No messages yet. Be the first to say something!</p>
                                        </div>
                                    </div>
                                ) : (
                                    messages
                                        .filter(msg =>
                                            searchQuery === '' ||
                                            msg.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                            msg.sender.toLowerCase().includes(searchQuery.toLowerCase())
                                        )
                                        .map((msg, idx) => (
                                            <div key={idx} className="flex gap-3 hover:bg-slate-100 dark:hover:bg-slate-900/50 -mx-2 px-2 py-2 rounded-lg transition-colors">
                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md">
                                                    {msg.sender[0].toUpperCase()}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-bold text-sm text-slate-900 dark:text-white">
                                                            {msg.sender}
                                                        </span>
                                                        <span className="text-xs text-slate-500">
                                                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-slate-700 dark:text-slate-300 break-words">
                                                        {msg.message}
                                                    </p>
                                                    {msg.file && (
                                                        <div className="mt-2">
                                                            <a
                                                                href={msg.file.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="inline-flex items-center gap-2 px-3 py-2 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-lg text-sm hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                                                            >
                                                                <Paperclip className="w-4 h-4" />
                                                                {msg.file.name}
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Message Input */}
                            {activeChannel && (
                                <div className="p-6 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                                    {selectedFile && (
                                        <div className="mb-3 flex items-center gap-2 px-4 py-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-sm text-purple-700 dark:text-purple-300">
                                            <Paperclip className="w-4 h-4" />
                                            <span className="flex-1 truncate">{selectedFile.name}</span>
                                            <button
                                                onClick={() => setSelectedFile(null)}
                                                className="text-red-500 hover:text-red-600 transition-colors"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileSelect}
                                            className="hidden"
                                        />
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            className="p-3 text-slate-600 hover:text-purple-600 dark:text-slate-400 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                                            title="Attach file"
                                        >
                                            <Paperclip className="w-5 h-5" />
                                        </button>
                                        <div className="flex-1 relative">
                                            <input
                                                type="text"
                                                value={messageInput}
                                                onChange={(e) => setMessageInput(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                                                placeholder={`Message #${activeChannel.name}...`}
                                                className="w-full px-4 py-3 bg-slate-100 dark:bg-slate-800 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-slate-900 dark:text-white placeholder-slate-500"
                                            />
                                        </div>
                                        <button
                                            onClick={sendMessage}
                                            disabled={!messageInput.trim() && !selectedFile}
                                            className="p-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all"
                                            title="Send message"
                                        >
                                            <Send className="w-5 h-5" />
                                        </button>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-2 ml-14">
                                        Press Enter to send, Shift+Enter for new line
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Create Channel Modal */}
                        {showChannelModal && (
                            <CreateChannelModal
                                onClose={() => setShowChannelModal(false)}
                                onCreate={createChannel}
                            />
                        )}

                        {/* User List Modal */}
                        {showUserList && (
                            <UserListModal
                                users={users}
                                onClose={() => setShowUserList(false)}
                                onSelectUser={startDirectMessage}
                            />
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

// Create Channel Modal Component
const CreateChannelModal = ({ onClose, onCreate }) => {
    const [channelName, setChannelName] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [description, setDescription] = useState('');

    const handleCreate = () => {
        if (!channelName.trim()) {
            toast.error('Channel name is required');
            return;
        }
        onCreate({
            name: channelName,
            private: isPrivate,
            description
        });
    };

    return (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-96" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Create Channel</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Channel Name
                        </label>
                        <input
                            type="text"
                            value={channelName}
                            onChange={(e) => setChannelName(e.target.value)}
                            placeholder="e.g., general"
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Description (optional)
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="What's this channel about?"
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows="3"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="private"
                            checked={isPrivate}
                            onChange={(e) => setIsPrivate(e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="private" className="text-sm text-slate-700 dark:text-slate-300">
                            Make private (invite-only)
                        </label>
                    </div>
                </div>
                <div className="flex gap-2 mt-6">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleCreate}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Create
                    </button>
                </div>
            </div>
        </div>
    );
};

// User List Modal Component
const UserListModal = ({ users, onClose, onSelectUser }) => {
    return (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-96" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Start Direct Message</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                    {users.map((user) => (
                        <button
                            key={user.id}
                            onClick={() => onSelectUser(user.id)}
                            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-left"
                        >
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold text-sm">
                                {user.name[0].toUpperCase()}
                            </div>
                            <div>
                                <div className="text-sm font-medium text-slate-900 dark:text-white">{user.name}</div>
                                <div className="text-xs text-slate-500">{user.email}</div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Chat;
